<script setup>
import { useAuthStore } from '@/stores/auth';
import { useActorHeadshotStore } from '@/stores/actor_headshots';
import SigninStep from '@/components/wizard/SigninStep.vue';
import HeadshotsStep from '@/components/wizard/HeadshotsStep.vue';
import SkillsStep from '@/components/wizard/SkillsStep.vue';
import ActorProfileStep from '@/components/wizard/ActorProfileStep.vue';
import VideosStep from '@/components/wizard/VideosStep.vue';
import ShowsStep from '@/components/wizard/ShowsStep.vue';
import FinishedStep from '@/components/wizard/FinishedStep.vue';
import ONavbar from '@/components/Navbar.vue';
import ONavbarItem from '@/components/NavbarItem.vue';
import { computed, onMounted, ref } from 'vue';
import { showStatus } from '@/dialogs';
import { useWizardActorEditing, WizardActorEditingKey } from '@/composables/wizard_actor_editing.js';
import { provideLocal, injectLocal } from '@vueuse/core';
import { FULL_HEIGHT_DISABLE_RULE } from '@/utilities/constants';
import { useMediaQuery } from '@vueuse/core';
import { resumeUrlsFor } from '@/utilities/actors.js';

const authStore = useAuthStore();
const actorHeadshotsStore = useActorHeadshotStore();

const ready = ref(false);
const authenticated = ref(false);
const actorId = ref(null);
const sessionEmail = ref(null);
const tabPage = ref('profile');
const showFinished = ref(false);

provideLocal(WizardActorEditingKey, useWizardActorEditing(actorId, sessionEmail));
const { isDirty, actor, saveActor, finishActor, registerSaveHook } = injectLocal(WizardActorEditingKey);


// refs
const mainTabsRef = ref(null);
const navbarRef = ref(null);
const profileStepRef = ref(null);

const isActorSaved = computed(() => {
  return !!actorId.value;
});

const isNarrowWidth = useMediaQuery(FULL_HEIGHT_DISABLE_RULE);

const hasRequiredHeadshots = computed(() => actorHeadshotsStore.forActorId(actorId.value).length > 0);

const hasResume = computed(() => {
  return resumeUrlsFor(actorId.value).length > 0 || !!profileStepRef.value?.pendingResume;
});

const helpMessages = computed(() => {
  if (!isActorSaved.value) return [];

  const messages = [];

  if (!hasRequiredHeadshots.value) {
    const item = { id: 'headshot', msg: 'Upload at least one headshot.' };
    if (tabPage.value !== 'headshots') {
      item.tab = 'headshots';
      if (isNarrowWidth.value) {
        item.msg += ' (Under the next menu)';
      } else {
        item.msg += 'On the Headshots tab.';
      }
    }
    messages.push(item);
  }

  if (!hasResume.value) {
    const item = { id: 'resume', msg: 'Upload your resume.' };
    if (tabPage.value !== 'profile') {
      item.tab = 'profile';
      if (isNarrowWidth.value) {
        item.msg += ' (Under the next menu)';
      } else {
        item.msg += ' On the personal information tab.';
      }
    }
    messages.push(item);
  }

  if (messages.length === 0 && isDirty.value) {
    messages.push({
      id: 'dirty',
      msg: 'Click save!',
      save: true,
    });
  }

  if (messages.length === 0 && actor.value.status === 'IN_WIZARD') {
    messages.push({
      id: 'submit',
      msg: 'Send for review!',
      submit: true,
    });
  }

  return messages;
});

const hasHelpMessages = computed(() => helpMessages.value.length > 0);

onMounted(() => {
  authStore.startup('wizard').then(() => {
    ready.value = true;
    if (authStore.valid) {
      signedin();
    }
  });
});

// methods

async function signedin() {
  authenticated.value = true;
  const { session, actor } = await authStore.retrieveSession({ withSkills: true });
  sessionEmail.value = session.wizard.email;
  actorId.value = actor?.id;
}

function actorSaved(actor) {
  if (actorId.value !== actor.id) {
    actorId.value = actor.id;
  }
  return true;
}

registerSaveHook(actorSaved);

async function saveAll() {
  const resp = await saveActor();
  if (resp) {
    showStatus({ message: 'Profile saved' });
  }
}

async function finishAndSubmit() {
  if (!actorId.value) return;

  if (await finishActor()) {
    showStatus({ message: 'Profile updates are submitted' });
    showFinished.value = true;
    sessionEmail.value = null;
    authenticated.value = false;
    await authStore.signOut();
    showStatus({ message: 'Signed out' });
  }
}

function switchTab(tab) {
  if (!tab.disabled) tabPage.value = tab.value;
}
</script>

<template lang="pug">
OLoading(:active="!ready")
FinishedStep(v-if="showFinished")
SigninStep(v-else-if="!authenticated" @signedin="signedin")
template(v-else)
  ONavbar(ref="navbarRef" fixed-top shadow)
    template(#brand)
      ONavbarItem(v-if="isDirty" tag="div")
        OButton(variant="danger" @click="saveAll") Save
        i.ml-2(v-if="isDirty") Unsaved changes
      ONavbarItem(v-else tag="div")
        OButton(
          v-tooltip="'Mark your profile as complete and ready for review'"
          variant="primary"
          @click="finishAndSubmit"
        ) Send for review
      ONavbarItem.is-clickable.is-hidden-desktop(tag="div")
        OButton(icon-right="angle-right" @click="navbarRef.toggleActive()") Next
    template(#start)
      ONavbarItem.navbar-tab(
        v-for="tab in mainTabsRef?.childItems"
        :key="tab.value"
        :class="{'navbar-tab-disabled': tab.disabled, 'navbar-tab-active': tab.value === tabPage}"
        @click="switchTab(tab)"
      )
        OIcon(v-if="tab.icon" :icon="tab.icon")
        | {{ tab.label }}
  br
  OTabs.hidden-tabs(
    ref="mainTabsRef"
    v-model="tabPage"
  )
    OTabItem(value="profile" icon="user" label="Personal information")
      ActorProfileStep(ref="profileStepRef")
      div(style="height: 5rem")
    OTabItem(value="headshots" :disabled="!isActorSaved" icon="user" label="Headshots")
      HeadshotsStep(v-if="isActorSaved" :actor-id="actorId")
      div(style="height: 5rem")
    OTabItem(value="skills" :disabled="!isActorSaved" icon="user" label="Skills")
      SkillsStep(v-if="isActorSaved")
      div(style="height: 5rem")
    OTabItem(value="videos" :disabled="!isActorSaved" icon="user" label="Videos/Reels")
      VideosStep(v-if="isActorSaved" :actor-id="actorId")
      div(style="height: 5rem")
    OTabItem(value="shows" :disabled="!isActorSaved" icon="user" label="Shows")
      ShowsStep(v-if="isActorSaved" :actor-id="actorId")
      div(style="height: 5rem")
    OTabItem(value="calendar" :disabled="!isActorSaved" icon="user" label="Calendar")
      p.notification.is-info.is-light
        | Coming soon
        br
        | You will be able to make off times which you are unavailable as well as see your calendar of assigned shows.
      div(style="height: 5rem")
  ONotification.bottom-sticky.is-light(
    v-model:active="hasHelpMessages"
    variant="warning"
    type="warning"
  )
    OButton.is-pulled-right(
      v-if="helpMessages.some(e => e.save)"
      variant="danger"
      @click="saveAll"
    ) Save
    OButton.is-pulled-right(
      v-if="helpMessages.some(e => e.submit)"
      variant="primary"
      @click="finishAndSubmit"
    ) Send
    ul
      li(v-for="item in helpMessages" :key="item.id")
        a.has-text-link(v-if="item.tab" @click.prevent="tabPage = item.tab") {{ item.linkText || 'Go' }}
        |
        | {{ item.msg }}
</template>

<style lang="scss">
@import "shared";
</style>

<style scoped lang="scss">
.b-tabs.hidden-tabs {
  :deep(nav.tabs) {
    display: none;
  }
}

.navbar {
  .navbar-tab {
    border: 1px solid transparent;
    margin-top: 0.5em;
  }

  .navbar-tab-active {
    border-color: #dbdbdb;
    border-bottom-color: transparent;
  }

  .navbar-tab-disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
}

.navbar-item {
  span.icon {
    margin-right: 0.5em;
  }
}

.bottom-sticky {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 30;

  ul {
    list-style: disc;
  }
}
</style>
